import { useEffect, useMemo, useState, type PropsWithChildren } from 'react'
import { ThemeContext } from './ThemeContext'
import {
  isThemePreference,
  resolveTheme,
  THEME_STORAGE_KEY,
  type ResolvedTheme,
  type ThemePreference,
} from './theme'

const darkThemeQuery = '(prefers-color-scheme: dark)'

function readStoredPreference(): ThemePreference {
  const storedPreference = window.localStorage.getItem(THEME_STORAGE_KEY)
  return isThemePreference(storedPreference) ? storedPreference : 'dark'
}

function systemPrefersDark() {
  return window.matchMedia(darkThemeQuery).matches
}

export function ThemeProvider({ children }: PropsWithChildren) {
  const [preference, setPreference] = useState<ThemePreference>(readStoredPreference)
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() =>
    resolveTheme(preference, systemPrefersDark()),
  )

  useEffect(() => {
    const mediaQuery = window.matchMedia(darkThemeQuery)

    const applyTheme = () => {
      const nextTheme = resolveTheme(preference, mediaQuery.matches)
      setResolvedTheme(nextTheme)
      document.documentElement.dataset.theme = nextTheme
      document.documentElement.style.colorScheme = nextTheme
      window.localStorage.setItem(THEME_STORAGE_KEY, preference)
    }

    applyTheme()
    mediaQuery.addEventListener('change', applyTheme)
    return () => {
      mediaQuery.removeEventListener('change', applyTheme)
    }
  }, [preference])

  const value = useMemo(() => ({ preference, resolvedTheme, setPreference }), [preference, resolvedTheme])

  return <ThemeContext value={value}>{children}</ThemeContext>
}
